import { Injectable } from '@angular/core'
import { Observable } from 'rxjs';
import { ResourceService } from 'app/shared/resources/resource.service';
import { ITravelWire, IHybridResult, IAgentProductList, TravelForm } from 'app/travel/travel.interfaces';
import { toParamsObj, tryParseLocalDate } from 'app/shared/utils'
import { Params } from '@angular/router';
import { FormGroup } from '@angular/forms';
import { ChildrenAges } from './travel.models';
import { LocalDate } from 'js-joda';
import { IVehicleTypes } from 'app/vehicles/vehicles.interfaces';
import { StatefulHttpService } from 'app/shared/stateful-http.service'
import { StatelessHttpService } from 'app/shared/stateless-http.service';

export interface ITravelParams {
    departureRoute: string
    code: string
    departureDate: LocalDate
    returnDate: LocalDate
    numberOfPassengers: number
    childAges: number[]
    vehicleType: string
}

@Injectable()
export class TravelService {

    constructor(
        private statelessHttpService: StatelessHttpService, 
        private statefulHttpService: StatefulHttpService, 
        private resourceService: ResourceService) 
    { }

    public getTravelFlow(salesChannel: string, p: ITravelParams): Observable<ITravelWire> {
        let params = {
            salesOwner: this.resourceService.getSalesOwner(),
            locale: this.resourceService.getLocale(),
            salesChannel,
            ...p
        }
        return this.statelessHttpService.get<ITravelWire>(false, 'travel-flow', { params: toParamsObj(params) })
    }

    public ensureAgentAuth() {
        this.statelessHttpService.ensureAuthTokenPresent()
        this.agentReferenceId()
    }

    private agentReferenceId() {
        let r = localStorage.getItem('sbw_AgentGenericId')
        if (!r) { throw new Error('agent id missing') }
        return r
    }

    public getAgentProducts(routeCode: string): Observable<IAgentProductList> {
        let params = {
            'salesOwner': this.resourceService.getSalesOwner(),
            'locale': this.resourceService.getLocale(),
            'agentGenericId': this.agentReferenceId(),
            'routeCode': routeCode
        }
        return this.statelessHttpService.get<IAgentProductList>(true, 'agent/products', { params: toParamsObj(params) })
    }

    public postHybridSimple(p: TravelParams): Observable<IHybridResult> {
        return this.statefulHttpService.post<IHybridResult>(false, 'travel-simple', p)
    }

    public postHybridFull(p: FullPostParams): Observable<IHybridResult> {
        return this.statefulHttpService.post<IHybridResult>(false, 'travel-full', p)
    }

}

export class TravelParams {
    routeOut: string
    depDateOut: Date
    depDateOutJoda: LocalDate
    routeHome: string
    depDateHome: Date
    depDateHomeJoda: LocalDate
    vty: string
    tty: string
    vehicleCount: number
    offer: string
    product: string
    adl?: number
    chd?: number
    inf?: number
    pet?: number
    chdAges?: number[]
    salesOwner: string
    locale: string
    miniBookId: string

    public any(): boolean {
        return !!(this.routeOut || this.depDateOut || this.routeHome || this.depDateHome || this.pet != undefined || this.vty
            || this.tty || this.offer || this.routeHome || this.adl != undefined || this.chd != undefined || this.inf != undefined)
    }

    public static fromQuery(qp: Params): TravelParams {
        let res = new TravelParams()
        let lower = {}
        for (let q in qp) lower[q.toLowerCase()] = qp[q]
        res.routeOut = lower['routeout']
        res.depDateOut = lower['depdateout'] && new Date(lower['depdateout'])
        res.depDateOutJoda = tryParseLocalDate(lower['depdateout'])

        res.routeHome = lower['routehome']
        res.depDateHome = lower['depdatehome'] && new Date(lower['depdatehome'])
        res.depDateHomeJoda = tryParseLocalDate(lower['depdatehome'])
        res.vty = TravelParams.vehicle(lower['vty'])
        res.tty = TravelParams.trailer(lower['vty'], lower['tty'])
        res.vehicleCount = lower['vehiclecount']
        res.offer = lower['offer']
        res.miniBookId = lower['minibookid']

        res.product = lower['product']

        res.adl = lower['adl'] && parseInt(lower['adl'])
        res.chd = lower['chd'] && parseInt(lower['chd'])
        res.inf = lower['inf'] && parseInt(lower['inf'])
        res.pet = lower['pet'] && parseInt(lower['pet'])

        let chdAges = lower['chdages']
        if (typeof chdAges == 'string') {
            chdAges = [chdAges]
        }
        res.chdAges = []
        if (chdAges) {
            for (let chdAge of chdAges)
                res.chdAges.push(parseInt(chdAge))

            let ca = ChildrenAges.fromStrings(chdAges, res.chd)
            res.chd = ca.children
            res.inf = ca.infants
        }

        res.salesOwner = lower['salesowner']
        res.locale = lower['locale']

        return res
    }

    static vehicle(vty: string) {
        return vty == 'VEHICLE_TRAILER' ? 'CAR' : vty
    }

    static trailer(vty: string, tty: string) {
        return vty == 'VEHICLE_TRAILER' ? 'YES' : tty
    }

}
export class PassengerCount {
    Adult?: number
    Child?: number
    Infant?: number
    Pet?: number
    childrenAges?: number[]
}

export class VehicleAndTrailerType {
    public registrationNumberMandatory: boolean
    public vehicleText: string
    public trailerText: string
    constructor(
        public vehicleType: string,
        public trailerType: string,
        public count: number,
    ) { }
}

export class FullPostParams {
    DepartureRouteId: string
    DepartureDate?: Date
    ReturnRouteId: string
    ReturnDate: Date
    PassengerCount: PassengerCount
    SuppliedCode: string
    VehicleAndTrailerTypes: VehicleAndTrailerType[]
    public static fromForm(form: FormGroup, returnRoute: string, vehiclesWire: IVehicleTypes): FullPostParams {
        const res = new FullPostParams()
        const value: TravelForm = form.value
        const ds = form.get('departureSelections')
        const passengers = form.get('passengerSelections')
        res.SuppliedCode = form.get('code').value
        res.DepartureRouteId = ds.get('outboundRouteCode').value
        res.DepartureDate = ds.get('outboundDepartureDate').value
        res.ReturnRouteId = returnRoute
        res.ReturnDate = ds.get('returnDepartureDate').value
        res.PassengerCount = new PassengerCount()
        const ca = ChildrenAges.fromStrings(passengers.get('childrenAges').value, passengers.get('childCount').value)
        res.PassengerCount.Adult = passengers.get('adultCount').value
        res.PassengerCount.Child = ca.children
        res.PassengerCount.Infant = ca.infants
        res.PassengerCount.Pet = passengers.get('petCount').value
        res.PassengerCount.childrenAges = ca.ages
        const vehicles = value.vehicleSelections
        const vehicleAndTrailerTypes = new VehicleAndTrailerType(vehicles && vehicles.vehicle, vehicles && vehicles.trailer, vehicles && vehicles.count)
        const vehicleType = vehiclesWire.vehicleTypes.find(v => v.code == (vehicles && vehicles.vehicle))
        if (vehicleType) {
            vehicleAndTrailerTypes.vehicleText = vehicleType.fullDescription // used on flow-passenger
            vehicleAndTrailerTypes.registrationNumberMandatory = vehicleType.registrationNumberMandatory
            const trailer = vehicleType.availableTrailers.find(t => t.code == vehicles.trailer)
            vehicleAndTrailerTypes.trailerText = trailer && trailer.fullDescription // used on flow-passenger
        }
        res.VehicleAndTrailerTypes = [vehicleAndTrailerTypes]
        return res
    }

}
